import { type RefObject, useEffect } from 'react';

export interface UseOutsideClickOptions {
  onClickOutside: () => void;
  ignoreSelectors?: string[];
  enabled?: boolean;
}

/**
 * 특정 요소 외부 클릭을 감지하는 훅
 * @param ref - 감지할 요소의 ref
 * @param options - 콜백, 무시할 선택자, 활성화 여부
 */
export const useOutsideClick = <T extends HTMLElement>(ref: RefObject<T>, options: UseOutsideClickOptions) => {
  const { onClickOutside, ignoreSelectors = [], enabled = true } = options;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (ref.current && ref.current.contains(target)) {
        return;
      }

      if (ignoreSelectors.length > 0) {
        const isIgnoredElement = ignoreSelectors.some((selector) => {
          const elements = document.querySelectorAll(selector);
          return Array.from(elements).some((element) => element.contains(target));
        });

        if (isIgnoredElement) {
          return;
        }
      }

      onClickOutside();
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, onClickOutside, ignoreSelectors, enabled]);
};
